import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  LinearProgress,
} from '@mui/material';
import { DialogTitleWithClose } from '../../common';
import PropTypes from 'prop-types';
import { debugWarn, debugError } from '../../../utils/debugLog';
import { useTemplateAnalyticsDetail } from '../hooks/useTemplateAnalyticsDetail';
import { useMvRefresh } from '../hooks/useMvRefresh';
import {
  TemplateInfoCard,
  VersionComparisonTable,
  VersionDetailsSection,
} from './TemplateAnalyticsDialogComponents';

/**
 * Template Analytics Dialog Component
 *
 * Displays comprehensive analytics for a template including:
 * - Execution statistics (total, success rate, timing)
 * - Cost analysis
 * - Feedback metrics (ratings, coverage)
 * - Performance indicators
 * - Content check results
 */
const TemplateAnalyticsDialog = ({
  open,
  onClose,
  templateId,
  templateName,
}) => {
  const { loading, error, data, fetchAnalytics, clearError, clearData } =
    useTemplateAnalyticsDetail();
  const { refreshing: mvRefreshing, refreshMv } = useMvRefresh();
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);

  useEffect(() => {
    if (open && templateId) {
      fetchAnalytics(templateId);
    } else if (!open) {
      clearError();
      clearData();
      setSelectedVersion(null);
    }
  }, [open, templateId, fetchAnalytics, clearError, clearData]);

  // Set selected version to latest when data loads
  useEffect(() => {
    if (data && data.version_analytics && data.version_analytics.length > 0) {
      // Find latest version (highest version_number)
      const latestVersion = data.version_analytics.reduce((latest, current) => {
        return (current.version_number || 0) > (latest.version_number || 0)
          ? current
          : latest;
      }, data.version_analytics[0]);
      setSelectedVersion(latestVersion);
    }
  }, [data]);

  const handleRefresh = async () => {
    if (!templateId) return;

    setRefreshLoading(true);

    try {
      // First, refresh the materialized view
      const refreshResult = await refreshMv();

      if (refreshResult.success) {
        // Then fetch fresh analytics data
        await fetchAnalytics(templateId);
      } else {
        // Even if refresh fails, still try to fetch with existing data
        debugWarn(
          'MV refresh failed, fetching with existing data:',
          refreshResult.message
        );
        await fetchAnalytics(templateId);
      }
    } catch (error) {
      debugError('Error during refresh:', error);
      // Still try to fetch even if refresh fails
      await fetchAnalytics(templateId);
    } finally {
      setRefreshLoading(false);
    }
  };

  const handleClose = () => {
    clearError();
    clearData();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitleWithClose
        title={
          <Box>
            <Typography variant="h6">Template Analytics</Typography>
            {templateName && (
              <Typography variant="body2" color="text.secondary">
                {templateName}
              </Typography>
            )}
          </Box>
        }
        onClose={handleClose}
        disableClose={loading || refreshLoading || mvRefreshing}
      />

      <DialogContent dividers>
        {loading && !data && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={4}
          >
            <CircularProgress size={48} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading analytics...
            </Typography>
          </Box>
        )}

        {(refreshLoading || mvRefreshing) && data && (
          <LinearProgress sx={{ mb: 2 }} />
        )}

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={handleRefresh}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {data && !loading && (
          <Box>
            <TemplateInfoCard data={data} />

            <VersionComparisonTable
              versionAnalytics={data.version_analytics}
              selectedVersion={selectedVersion}
              onVersionSelect={setSelectedVersion}
            />

            <VersionDetailsSection selectedVersion={selectedVersion} />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleRefresh}
          disabled={loading || refreshLoading || mvRefreshing}
        >
          {mvRefreshing
            ? 'Refreshing MV...'
            : refreshLoading
              ? 'Loading...'
              : 'Refresh'}
        </Button>
        <Button onClick={handleClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

TemplateAnalyticsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  templateId: PropTypes.string.isRequired,
  templateName: PropTypes.string,
};

export default TemplateAnalyticsDialog;
